import { useState, useRef, useCallback } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { ProTeaserSection } from "@/components/pro-gate";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { useQueryClient } from "@tanstack/react-query";
import {
  getListCustomersQueryKey,
  getListProductsQueryKey,
  getListInternalCatalogQueryKey,
  useListInvoices,
  useListEstimates,
  getInvoice,
  getEstimate,
  useGetSettings,
  getGetSettingsQueryKey,
} from "@workspace/api-client-react";
import {
  Upload, Download, FileText, AlertCircle, CheckCircle2,
  Loader2, X, ChevronRight, Users, Package, Wrench,
  Calendar, Info, FileSpreadsheet,
} from "lucide-react";
import logoQuickbooks from "@/assets/logo-quickbooks.png";
import { PdfInvoiceImport } from "@/components/pdf-invoice-import";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  generateQuickBooksCSV,
  generateQuickBooksIIF,
  downloadFile,
  type ExportableInvoice,
} from "@/lib/quickbooks-export";

// Guardrails for uploaded import files: the whole file is parsed in-memory and
// POSTed as a single JSON body, so cap both the raw size and the row count to
// keep the browser responsive and the request reasonable. Larger lists should
// be split into multiple files.
const MAX_IMPORT_FILE_BYTES = 2 * 1024 * 1024; // 2 MB
const MAX_IMPORT_ROWS = 500;

type RowError = { row: number; message: string };
type ImportResult = { created: number; skipped: number; errors: RowError[] };

type ParsedRow = Record<string, string>;

type TabId = "pdf" | "customers" | "products" | "cost-catalog";

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ElementType;
  columns: string[];
  requiredColumns: string[];
  exampleRow: Record<string, string>;
  endpoint: string;
  rowMapper: (row: ParsedRow) => Record<string, unknown>;
}

const CUSTOMER_COLS = ["name", "email", "phone", "addressLine1", "addressLine2", "city", "state", "postalCode", "country", "shippingAddressLine1", "shippingAddressLine2", "shippingCity", "shippingState", "shippingPostalCode", "shippingCountry", "paymentTermsDays", "taxExempt", "notes"];
const PRODUCT_COLS = ["name", "description", "unitPrice", "unit", "category", "sku", "taxable", "productType", "linkedCosts"];
const CATALOG_COLS = ["name", "description", "unitCost", "unit", "category", "type"];

const TAB_CONFIGS: TabConfig[] = [
  {
    id: "customers",
    label: "Customers",
    icon: Users,
    columns: CUSTOMER_COLS,
    requiredColumns: ["name"],
    exampleRow: {
      name: "Acme Corp",
      email: "contact@acme.com",
      phone: "555-1234",
      addressLine1: "123 Main St",
      addressLine2: "",
      city: "Springfield",
      state: "IL",
      postalCode: "62701",
      country: "US",
      shippingAddressLine1: "",
      shippingAddressLine2: "",
      shippingCity: "",
      shippingState: "",
      shippingPostalCode: "",
      shippingCountry: "",
      paymentTermsDays: "30",
      taxExempt: "false",
      notes: "",
    },
    endpoint: "/api/customers/import",
    rowMapper: (row) => ({
      name: row.name,
      email: row.email || undefined,
      phone: row.phone || undefined,
      addressLine1: row.addressLine1 || row.address || undefined,
      addressLine2: row.addressLine2 || undefined,
      city: row.city || undefined,
      state: row.state || undefined,
      postalCode: row.postalCode || undefined,
      country: row.country || undefined,
      shippingAddressLine1: row.shippingAddressLine1 || undefined,
      shippingAddressLine2: row.shippingAddressLine2 || undefined,
      shippingCity: row.shippingCity || undefined,
      shippingState: row.shippingState || undefined,
      shippingPostalCode: row.shippingPostalCode || undefined,
      shippingCountry: row.shippingCountry || undefined,
      paymentTermsDays: row.paymentTermsDays ? parseInt(row.paymentTermsDays) : undefined,
      taxExempt: row.taxExempt === "true" ? true : row.taxExempt === "false" ? false : undefined,
      notes: row.notes || undefined,
    }),
  },
  {
    id: "products",
    label: "Products & Services",
    icon: Package,
    columns: PRODUCT_COLS,
    requiredColumns: ["name", "unitPrice"],
    exampleRow: {
      name: "Framing Labor",
      description: "Standard wall framing per sqft",
      unitPrice: "45.00",
      unit: "sqft",
      category: "Labor",
      sku: "SVC-001",
      taxable: "true",
      productType: "service",
      linkedCosts: "Labor - Standard Rate",
    },
    endpoint: "/api/products/import",
    rowMapper: (row) => ({
      name: row.name,
      description: row.description || undefined,
      unitPrice: row.unitPrice,
      unit: row.unit || undefined,
      category: row.category || undefined,
      sku: row.sku || undefined,
      taxable: row.taxable ? row.taxable.toLowerCase() !== "false" : undefined,
      productType: row.productType || undefined,
      linkedCosts: row.linkedCosts || undefined,
    }),
  },
  {
    id: "cost-catalog",
    label: "Cost Catalog",
    icon: Wrench,
    columns: CATALOG_COLS,
    requiredColumns: ["name", "unitCost"],
    exampleRow: {
      name: "Lumber 2x4x8",
      description: "Dimensional framing lumber",
      unitCost: "8.50",
      unit: "each",
      category: "Materials",
      type: "materials",
    },
    endpoint: "/api/internal-catalog/import",
    rowMapper: (row) => ({
      name: row.name,
      description: row.description || undefined,
      unitCost: row.unitCost,
      unit: row.unit || undefined,
      category: row.category || undefined,
      type: row.type || undefined,
    }),
  },
];

function downloadTemplate(config: TabConfig) {
  const headers = config.columns.join(",");
  const example = config.columns.map((col) => {
    const val = config.exampleRow[col] ?? "";
    return val.includes(",") ? `"${val}"` : val;
  }).join(",");
  const csv = `${headers}\n${example}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${config.id}-template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function parseFileToRows(file: File): Promise<ParsedRow[]> {
  return new Promise((resolve, reject) => {
    const name = file.name.toLowerCase();
    if (name.endsWith(".csv") || name.endsWith(".tsv") || name.endsWith(".txt")) {
      Papa.parse<ParsedRow>(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
        transform: (v) => v.trim(),
        complete: (result) => resolve(result.data),
        error: (err) => reject(new Error(err.message)),
      });
    } else if (name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".ods")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target!.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<ParsedRow>(ws, { defval: "" });
          const cleaned = rows.map((r) => {
            const out: ParsedRow = {};
            for (const k of Object.keys(r)) {
              out[k.trim()] = String(r[k]).trim();
            }
            return out;
          });
          resolve(cleaned);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    } else {
      reject(new Error("Unsupported file type. Please upload a CSV or XLSX file."));
    }
  });
}

interface ImportTabProps {
  config: TabConfig;
  onImportSuccess: () => void;
}

function ImportTab({ config, onImportSuccess }: ImportTabProps) {
  const { toast } = useToast();
  const [parsedRows, setParsedRows] = useState<ParsedRow[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<RowError[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setParsedRows(null);
    setParseError(null);
    setRowErrors([]);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFile = useCallback(async (file: File) => {
    setParseError(null);
    setImportResult(null);
    setRowErrors([]);
    setParsedRows(null);
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      setParseError(
        `This file is ${sizeMb} MB. Please upload a file under 2 MB — split larger lists into multiple files.`,
      );
      return;
    }
    try {
      const rows = await parseFileToRows(file);
      if (rows.length === 0) {
        setParseError("The file contains no data rows.");
        return;
      }
      if (rows.length > MAX_IMPORT_ROWS) {
        setParseError(
          `This file has ${rows.length.toLocaleString()} rows. Please import at most ${MAX_IMPORT_ROWS} rows at a time — split it into smaller files.`,
        );
        return;
      }
      setParsedRows(rows);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Failed to parse file.");
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleConfirmImport = async () => {
    if (!parsedRows) return;
    setImporting(true);
    setRowErrors([]);
    try {
      const rows = parsedRows.map(config.rowMapper);
      const res = await fetch(config.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `Server error ${res.status}`);
      }
      const result: ImportResult = await res.json();
      setImportResult(result);
      setRowErrors(result.errors);
      onImportSuccess();
      const skippedMsg = result.skipped > 0 ? ` ${result.skipped} record${result.skipped !== 1 ? "s" : ""} skipped (already exist).` : "";
      toast({
        title: `Import complete`,
        description: `${result.created} record${result.created !== 1 ? "s" : ""} created.${skippedMsg}${result.errors.length > 0 ? ` ${result.errors.length} row${result.errors.length !== 1 ? "s" : ""} had errors.` : ""}`,
      });
    } catch (err) {
      toast({
        title: "Import failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const visibleColumns = parsedRows
    ? config.columns.filter((col) => parsedRows.some((r) => col in r))
    : config.columns;

  return (
    <div className="space-y-6">
      <Card className="border-dashed border-border/70">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-medium text-sm">Download Template</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              CSV file with the correct column headers and an example row.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 shrink-0"
            onClick={() => downloadTemplate(config)}
          >
            <Download className="w-4 h-4" />
            Download {config.id}-template.csv
          </Button>
        </CardContent>
      </Card>

      {config.id === "products" && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground -mt-3">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          The <span className="font-medium">linkedCosts</span> column accepts one or more cost catalog item names (comma-separated), e.g. <span className="italic">Labor - Standard Rate, Concrete Mix</span>. These entries must match EXISTING cost catalog items EXACTLY.
        </p>
      )}

      {!parsedRows && (
        <div
          className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
            dragOver
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50 hover:bg-muted/30"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.ods,.tsv,.txt"
            className="hidden"
            onChange={handleInputChange}
          />
          <Upload className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="font-medium text-sm">Drag & drop a file here, or click to browse</p>
          <p className="text-xs text-muted-foreground mt-1">
            Supports CSV, Excel (.xlsx / .xls), and ODS files · up to 2 MB and {MAX_IMPORT_ROWS} rows
          </p>
        </div>
      )}

      {parseError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{parseError}</AlertDescription>
        </Alert>
      )}

      {parsedRows && !importResult && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium text-sm">
                Preview — {parsedRows.length} row{parsedRows.length !== 1 ? "s" : ""}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={reset} className="gap-1.5 text-muted-foreground">
              <X className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>

          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 text-center">#</TableHead>
                  {visibleColumns.map((col) => (
                    <TableHead key={col}>
                      {col}
                      {config.requiredColumns.includes(col) && (
                        <span className="text-destructive ml-0.5">*</span>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {parsedRows.map((row, i) => (
                  <TableRow key={i} className={rowErrors.some((e) => e.row === i) ? "bg-destructive/5" : ""}>
                    <TableCell className="text-center text-xs text-muted-foreground">{i + 1}</TableCell>
                    {visibleColumns.map((col) => (
                      <TableCell key={col} className="text-sm">
                        {row[col] ?? ""}
                        {!row[col] && config.requiredColumns.includes(col) && (
                          <span className="text-destructive text-xs">(missing)</span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {rowErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <span className="font-medium">{rowErrors.length} row{rowErrors.length !== 1 ? "s" : ""} with validation errors:</span>
                <ul className="mt-1 space-y-0.5 list-disc list-inside text-xs">
                  {rowErrors.map((e) => (
                    <li key={e.row}>Row {e.row + 1}: {e.message}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={reset}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={importing}
              className="gap-2"
            >
              {importing ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Importing...</>
              ) : (
                <><ChevronRight className="w-4 h-4" />Confirm Import ({parsedRows.length} rows)</>
              )}
            </Button>
          </div>
        </div>
      )}

      {importResult && (
        <div className="space-y-4">
          <Alert className="border-green-500/50 bg-green-50 dark:bg-green-950/20 text-green-800 dark:text-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription>
              <span className="font-medium">{importResult.created} record{importResult.created !== 1 ? "s" : ""} created successfully.</span>
              {importResult.errors.length > 0 && (
                <span className="ml-2 text-amber-700 dark:text-amber-300">
                  {importResult.errors.length} row{importResult.errors.length !== 1 ? "s" : ""} skipped due to errors.
                </span>
              )}
            </AlertDescription>
          </Alert>

          {importResult.skipped > 0 && (
            <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription>
                <span className="font-medium">{importResult.skipped} record{importResult.skipped !== 1 ? "s" : ""} skipped (already exist).</span>
              </AlertDescription>
            </Alert>
          )}

          {importResult.errors.length > 0 && (
            <div className="rounded-lg border overflow-hidden">
              <div className="px-4 py-2 bg-muted/50 border-b">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Rows with Errors
                </p>
              </div>
              <div className="divide-y">
                {importResult.errors.map((e) => (
                  <div key={e.row} className="flex items-start gap-3 px-4 py-2.5">
                    <Badge variant="destructive" className="text-xs mt-0.5 shrink-0">Row {e.row + 1}</Badge>
                    <p className="text-sm text-muted-foreground">{e.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button variant="outline" onClick={reset} className="gap-2">
              <Upload className="w-4 h-4" />
              Import Another File
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function QuickBooksExportSection() {
  const { toast } = useToast();
  const [format, setFormat] = useState<"csv" | "iif">("csv");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const queryParams: Record<string, string | undefined> = {};
  if (statusFilter !== "all") queryParams.status = statusFilter;
  if (startDate) queryParams.startDate = startDate;
  if (endDate) queryParams.endDate = endDate;

  const { data: invoices, isLoading } = useListInvoices(queryParams);

  const handleExport = async () => {
    if (!invoices || invoices.length === 0) {
      toast({
        title: "No invoices to export",
        description: "No invoices match the current filters.",
        variant: "destructive",
      });
      return;
    }

    setExporting(true);
    try {
      const fullInvoices: ExportableInvoice[] = await Promise.all(
        invoices.map(async (inv) => {
          const detail = await getInvoice(inv.id);
          return {
            id: detail.id,
            invoiceNumber: detail.invoiceNumber,
            title: detail.title,
            status: detail.status,
            customerName: detail.customerName,
            customerEmail: detail.customerEmail,
            customerPhone: detail.customerPhone,
            description: detail.description,
            total: detail.total,
            subtotal: detail.subtotal,
            taxAmount: detail.taxAmount,
            taxEnabled: detail.taxEnabled,
            taxRate: detail.taxRate,
            selectedTaxLines: detail.selectedTaxLines,
            depositType: detail.depositType,
            depositValue: detail.depositValue,
            invoiceDate: detail.invoiceDate,
            dueDate: detail.dueDate,
            createdAt: String(detail.createdAt),
            lineItems: (detail.lineItems || []).map((li) => ({
              description: li.description,
              productName: li.productName,
              quantity: li.quantity,
              unitPrice: li.unitPrice,
              total: li.total,
            })),
          };
        }),
      );

      const today = new Date().toISOString().slice(0, 10);
      if (format === "csv") {
        const content = generateQuickBooksCSV(fullInvoices);
        downloadFile(content, `quickbooks-invoices-${today}.csv`, "text/csv;charset=utf-8;");
      } else {
        const content = generateQuickBooksIIF(fullInvoices);
        downloadFile(content, `quickbooks-invoices-${today}.iif`, "application/octet-stream");
      }

      toast({
        title: "Export complete",
        description: `${fullInvoices.length} invoice${fullInvoices.length !== 1 ? "s" : ""} exported as ${format === "csv" ? "CSV" : "IIF"}.`,
      });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl border border-border/50 bg-white dark:bg-white/10">
              <img src={logoQuickbooks} alt="QuickBooks" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <h3 className="font-semibold text-base">QuickBooks Invoice Export</h3>
              <p className="text-sm text-muted-foreground">
                Export invoices for import into QuickBooks Online or Desktop.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="export-format">Format</Label>
              <Select value={format} onValueChange={(v) => setFormat(v as "csv" | "iif")}>
                <SelectTrigger id="export-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">QuickBooks Online (CSV)</SelectItem>
                  <SelectItem value="iif">QuickBooks Desktop (IIF)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="export-status">Status Filter</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="export-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="void">Void</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="export-start-date">Start Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="export-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="export-end-date">End Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="export-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {isLoading ? (
                "Loading invoices..."
              ) : invoices ? (
                `${invoices.length} invoice${invoices.length !== 1 ? "s" : ""} match the current filters`
              ) : (
                "No invoices found"
              )}
            </p>
            <Button
              onClick={handleExport}
              disabled={exporting || isLoading || !invoices || invoices.length === 0}
              className="gap-2"
            >
              {exporting ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Exporting...</>
              ) : (
                <><Download className="w-4 h-4" />Download {format === "csv" ? "CSV" : "IIF"}</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function formatCsvDate(d: string | Date | null | undefined): string {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function SpreadsheetCsvExportSection() {
  const { toast } = useToast();
  const [exportType, setExportType] = useState<"estimates" | "invoices">("estimates");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);

  const estimateQueryParams: Record<string, string | undefined> = {};
  if (exportType === "estimates" && statusFilter !== "all") estimateQueryParams.status = statusFilter;
  if (startDate) estimateQueryParams.startDate = startDate;
  if (endDate) estimateQueryParams.endDate = endDate;

  const invoiceQueryParams: Record<string, string | undefined> = {};
  if (exportType === "invoices" && statusFilter !== "all") invoiceQueryParams.status = statusFilter;
  if (startDate) invoiceQueryParams.startDate = startDate;
  if (endDate) invoiceQueryParams.endDate = endDate;

  const { data: estimates, isLoading: estimatesLoading } = useListEstimates(estimateQueryParams);
  const { data: invoices, isLoading: invoicesLoading } = useListInvoices(invoiceQueryParams);
  const { data: companySettings } = useGetSettings({ query: { queryKey: getGetSettingsQueryKey() } });

  const isLoading = exportType === "estimates" ? estimatesLoading : invoicesLoading;
  const items = exportType === "estimates" ? estimates : invoices;

  const handleExport = async () => {
    if (!items || items.length === 0) {
      toast({
        title: `No ${exportType === "estimates" ? "quotes" : "invoices"} to export`,
        description: `No ${exportType === "estimates" ? "quotes" : "invoices"} match the current filters.`,
        variant: "destructive",
      });
      return;
    }

    setExporting(true);
    try {
      const parseTaxLinesLocal = (raw: string | null | undefined): Array<{ name: string; rate: number }> => {
        if (!raw) return [];
        try {
          const parsed = JSON.parse(raw as string);
          if (!Array.isArray(parsed)) return [];
          return parsed.filter((t: unknown): t is { name: string; rate: number } => {
            if (typeof t !== "object" || t === null) return false;
            const obj = t as Record<string, unknown>;
            return typeof obj.name === "string" && typeof obj.rate === "number";
          });
        } catch { return []; }
      };

      const rows: string[][] = [];
      const today = new Date().toISOString().slice(0, 10);

      if (exportType === "estimates") {
        const fullEstimates = await Promise.all(
          (items as typeof estimates)!.map((est) => getEstimate(est.id)),
        );

        const taxNames: string[] = [];
        const taxNameSet = new Set<string>();

        // Seed from account-level tax lines so all configured taxes always appear as columns
        for (const tl of parseTaxLinesLocal(companySettings?.taxLines)) {
          if (!taxNameSet.has(tl.name)) { taxNameSet.add(tl.name); taxNames.push(tl.name); }
        }

        // Layer in any additional names found on individual estimates
        for (const est of fullEstimates) {
          for (const tl of parseTaxLinesLocal(est.selectedTaxLines)) {
            if (!taxNameSet.has(tl.name)) { taxNameSet.add(tl.name); taxNames.push(tl.name); }
          }
        }

        // Add synthetic columns for legacy single-rate estimates (taxEnabled but no selectedTaxLines)
        for (const est of fullEstimates) {
          const estLegacy = est as typeof est & { taxEnabled?: boolean; taxRate?: number };
          if (estLegacy.taxEnabled && parseTaxLinesLocal(est.selectedTaxLines).length === 0 && Number(estLegacy.taxRate) > 0) {
            const colName = `Tax (${estLegacy.taxRate}%)`;
            if (!taxNameSet.has(colName)) { taxNameSet.add(colName); taxNames.push(colName); }
          }
        }

        const headers = [
          "Quote Number", "Customer", "Date", "Status", "Subtotal",
          ...taxNames,
          "Total", "Line Description", "Quantity", "Unit Price", "Line Total",
        ];

        for (const est of fullEstimates) {
          const estAny = est as typeof est & { subtotal?: number; taxEnabled?: boolean; taxRate?: number };
          const number = est.estimateNumber || `EST-${est.id}`;
          const customer = est.customerName || "";
          const date = formatCsvDate(est.estimateDate || est.createdAt);
          const status = est.status || "";
          const computedSubtotal = (est.lineItems || []).reduce(
            (sum, li) => sum + Number(li.quantity) * Number(li.unitPrice), 0,
          );
          const subtotal = estAny.subtotal ?? computedSubtotal;
          const taxLines = parseTaxLinesLocal(est.selectedTaxLines);
          const taxAmountsByName = new Map(taxLines.map((tl) => [tl.name, subtotal * (tl.rate / 100)]));

          // Handle legacy single-rate estimates
          if (estAny.taxEnabled && taxLines.length === 0 && Number(estAny.taxRate) > 0) {
            const colName = `Tax (${estAny.taxRate}%)`;
            taxAmountsByName.set(colName, subtotal * (Number(estAny.taxRate) / 100));
          }

          const computedTax = taxLines.length > 0
            ? taxLines.reduce((sum, tl) => sum + subtotal * (tl.rate / 100), 0)
            : (estAny.taxEnabled ? subtotal * (Number(estAny.taxRate ?? 0) / 100) : 0);
          const total = est.total ?? subtotal + computedTax;
          const taxCells = taxNames.map((name) =>
            taxAmountsByName.has(name) ? taxAmountsByName.get(name)!.toFixed(2) : "",
          );
          const blankTaxCells = taxNames.map(() => "");

          if (!est.lineItems || est.lineItems.length === 0) {
            rows.push([number, customer, date, status, subtotal.toFixed(2), ...taxCells, total.toFixed(2), "", "", "", ""]);
          } else {
            est.lineItems.forEach((li, idx) => {
              const desc = li.productName ? `${li.productName} - ${li.description}` : li.description;
              rows.push([
                number, customer, date, status,
                idx === 0 ? subtotal.toFixed(2) : "",
                ...(idx === 0 ? taxCells : blankTaxCells),
                idx === 0 ? total.toFixed(2) : "",
                desc, String(li.quantity), Number(li.unitPrice).toFixed(2), Number(li.total).toFixed(2),
              ]);
            });
          }
        }

        const csv = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(",")).join("\r\n") + "\r\n";
        downloadFile(csv, `quotrak-quotes-${today}.csv`, "text/csv;charset=utf-8;");
      } else {
        const fullInvoices = await Promise.all(
          (items as typeof invoices)!.map((inv) => getInvoice(inv.id)),
        );

        const taxNames: string[] = [];
        const taxNameSet = new Set<string>();

        // Seed from account-level tax lines so all configured taxes always appear as columns
        for (const tl of parseTaxLinesLocal(companySettings?.taxLines)) {
          if (!taxNameSet.has(tl.name)) { taxNameSet.add(tl.name); taxNames.push(tl.name); }
        }

        // Layer in any additional names found on individual invoices
        for (const inv of fullInvoices) {
          for (const tl of parseTaxLinesLocal(inv.selectedTaxLines)) {
            if (!taxNameSet.has(tl.name)) { taxNameSet.add(tl.name); taxNames.push(tl.name); }
          }
        }

        // Add synthetic columns for legacy single-rate invoices (taxEnabled but no selectedTaxLines)
        for (const inv of fullInvoices) {
          const invLegacy = inv as typeof inv & { taxEnabled?: boolean; taxRate?: number };
          if (invLegacy.taxEnabled && parseTaxLinesLocal(inv.selectedTaxLines).length === 0 && Number(invLegacy.taxRate) > 0) {
            const colName = `Tax (${invLegacy.taxRate}%)`;
            if (!taxNameSet.has(colName)) { taxNameSet.add(colName); taxNames.push(colName); }
          }
        }

        const headers = [
          "Invoice Number", "Customer", "Date", "Status", "Subtotal",
          ...taxNames,
          "Total", "Line Description", "Quantity", "Unit Price", "Line Total",
        ];

        for (const inv of fullInvoices) {
          const invAny = inv as typeof inv & { taxEnabled?: boolean; taxRate?: number };
          const number = inv.invoiceNumber || `INV-${inv.id}`;
          const customer = inv.customerName || "";
          const date = formatCsvDate(inv.invoiceDate || inv.createdAt);
          const status = inv.status || "";
          const computedSubtotal = (inv.lineItems || []).reduce(
            (sum, li) => sum + Number(li.quantity) * Number(li.unitPrice), 0,
          );
          const subtotal = inv.subtotal ?? computedSubtotal;
          const taxLines = parseTaxLinesLocal(inv.selectedTaxLines);
          const taxAmountsByName = new Map(taxLines.map((tl) => [tl.name, subtotal * (tl.rate / 100)]));

          // Handle legacy single-rate invoices
          if (invAny.taxEnabled && taxLines.length === 0 && Number(invAny.taxRate) > 0) {
            const colName = `Tax (${invAny.taxRate}%)`;
            taxAmountsByName.set(colName, subtotal * (Number(invAny.taxRate) / 100));
          }

          const computedTax = taxLines.length > 0
            ? taxLines.reduce((sum, tl) => sum + subtotal * (tl.rate / 100), 0)
            : (invAny.taxEnabled ? subtotal * (Number(invAny.taxRate ?? 0) / 100) : 0);
          const total = inv.total ?? subtotal + computedTax;
          const taxCells = taxNames.map((name) =>
            taxAmountsByName.has(name) ? taxAmountsByName.get(name)!.toFixed(2) : "",
          );
          const blankTaxCells = taxNames.map(() => "");

          if (!inv.lineItems || inv.lineItems.length === 0) {
            rows.push([number, customer, date, status, subtotal.toFixed(2), ...taxCells, total.toFixed(2), "", "", "", ""]);
          } else {
            inv.lineItems.forEach((li, idx) => {
              const desc = li.productName ? `${li.productName} - ${li.description}` : li.description;
              rows.push([
                number, customer, date, status,
                idx === 0 ? subtotal.toFixed(2) : "",
                ...(idx === 0 ? taxCells : blankTaxCells),
                idx === 0 ? total.toFixed(2) : "",
                desc, String(li.quantity), Number(li.unitPrice).toFixed(2), Number(li.total).toFixed(2),
              ]);
            });
          }
        }

        const csv = [headers, ...rows].map((row) => row.map(escapeCsvCell).join(",")).join("\r\n") + "\r\n";
        downloadFile(csv, `quotrak-invoices-${today}.csv`, "text/csv;charset=utf-8;");
      }

      toast({
        title: "Export complete",
        description: `${items.length} ${exportType === "estimates" ? "quote" : "invoice"}${items.length !== 1 ? "s" : ""} downloaded as CSV.`,
      });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const estimateStatuses = [
    { value: "all", label: "All Statuses" },
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
    { value: "accepted", label: "Accepted" },
    { value: "declined", label: "Declined" },
    { value: "converted", label: "Converted" },
  ];

  const invoiceStatuses = [
    { value: "all", label: "All Statuses" },
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
    { value: "paid", label: "Paid" },
    { value: "overdue", label: "Overdue" },
    { value: "void", label: "Void" },
  ];

  const statuses = exportType === "estimates" ? estimateStatuses : invoiceStatuses;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-xl border border-border/50 bg-muted">
              <FileSpreadsheet className="w-7 h-7 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-base">Spreadsheet (CSV) Export</h3>
              <p className="text-sm text-muted-foreground">
                Download quotes or invoices as a CSV file you can open in Excel, Numbers, or any spreadsheet app.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="csv-export-type">Export Type</Label>
              <Select value={exportType} onValueChange={(v) => { setExportType(v as "estimates" | "invoices"); setStatusFilter("all"); }}>
                <SelectTrigger id="csv-export-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="estimates">Quotes</SelectItem>
                  <SelectItem value="invoices">Invoices</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="csv-export-status">Status Filter</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger id="csv-export-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="csv-start-date">Start Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="csv-start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="csv-end-date">End Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="csv-end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {isLoading ? (
                "Loading..."
              ) : items ? (
                `${items.length} ${exportType === "estimates" ? "quote" : "invoice"}${items.length !== 1 ? "s" : ""} match the current filters`
              ) : (
                `No ${exportType === "estimates" ? "quotes" : exportType} found`
              )}
            </p>
            <Button
              onClick={handleExport}
              disabled={exporting || isLoading || !items || items.length === 0}
              className="gap-2"
            >
              {exporting ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Exporting...</>
              ) : (
                <><Download className="w-4 h-4" />Download CSV</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ImportsSection({ onImportSuccess }: { onImportSuccess: () => void }) {
  return (
    <div className="space-y-6">
      <Tabs defaultValue="customers">
        <TabsList className="mb-6 w-full h-auto p-1.5 flex-wrap">
          {TAB_CONFIGS.map((config) => (
            <TabsTrigger key={config.id} value={config.id} className="flex-1 justify-center gap-2 py-2 text-base">
              <config.icon className="w-4 h-4" />
              {config.label}
            </TabsTrigger>
          ))}
          <TabsTrigger value="pdf" className="flex-1 justify-center gap-2 py-2 text-base">
            <FileText className="w-4 h-4" />
            From PDF
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pdf">
          <PdfInvoiceImport />
        </TabsContent>

        {TAB_CONFIGS.map((config) => (
          <TabsContent key={config.id} value={config.id}>
            <div className="mb-4">
              <p className="text-muted-foreground text-sm">
                Bulk-import {config.label.toLowerCase()} from a CSV or Excel file.
              </p>
            </div>
            <ImportTab config={config} onImportSuccess={onImportSuccess} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export default function ImportExportPage() {
  usePageTitle("Import & Export");
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<"imports" | "exports">("exports");

  const handleImportSuccess = () => {
    queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListInternalCatalogQueryKey() });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Upload className="w-8 h-8 text-primary" />
            Import & Export
          </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Import data from files or export to spreadsheets.
        </p>
      </div>

      <ProTeaserSection
        title="Import & Export"
        description="Import customers, products, and costs from CSV — or export your data to QuickBooks, Xero, and Google Sheets."
      >
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-800 dark:text-blue-300">
            Tip: You can download several quotes as PDFs in a ZIP from the Quotes page.
          </AlertDescription>
        </Alert>

        <Tabs value={activeSection} onValueChange={(v) => setActiveSection(v as "imports" | "exports")}>
          <TabsList className="mb-6 w-full h-auto p-1.5">
            <TabsTrigger value="exports" className="flex-1 justify-center gap-2 py-2 text-base">
              <Upload className="w-4 h-4" />
              Export
            </TabsTrigger>
            <TabsTrigger value="imports" className="flex-1 justify-center gap-2 py-2 text-base">
              <Download className="w-4 h-4" />
              Import
            </TabsTrigger>
          </TabsList>

          <TabsContent value="imports">
            <ImportsSection onImportSuccess={handleImportSuccess} />
          </TabsContent>

          <TabsContent value="exports">
            <div className="space-y-8">
              <QuickBooksExportSection />
              <SpreadsheetCsvExportSection />
            </div>
          </TabsContent>
        </Tabs>
      </ProTeaserSection>
    </div>
  );
}
